/**
 * Who is signed in, and the gate that keeps the rest of the app from rendering
 * before we know.
 */
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { boot, googleProvider, type ServerConfig } from './boot'

interface Session {
  user: User | null
  config: ServerConfig
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
}

const SessionContext = createContext<Session | null>(null)

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession outside SessionProvider')
  return session
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ServerConfig | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void boot()
      .then(({ config, auth }) => {
        if (cancelled) return
        setConfig(config)
        return onAuthStateChanged(auth, (next) => {
          setUser(next)
          setReady(true)
        })
      })
      .catch((error: Error) => setFailure(error.message))
    return () => {
      cancelled = true
    }
  }, [])

  if (failure) {
    return (
      <Splash>
        <p className="text-rose-300">{failure}</p>
        <button className="btn-ghost mt-4" onClick={() => location.reload()}>
          Try again
        </button>
      </Splash>
    )
  }

  if (!config || !ready) return <Splash>Uncorking…</Splash>

  const session: Session = {
    user,
    config,
    signIn: async () => {
      const { auth, config } = await boot()
      if (config.emulators?.auth) return signInToEmulator(auth)
      await signInWithPopup(auth, googleProvider)
    },
    signOutNow: async () => {
      const { auth } = await boot()
      await signOut(auth)
    },
  }

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
}

/**
 * Sign in against the Auth emulator without a popup.
 *
 * `signInWithPopup` opens Google's real account chooser, which no emulator can
 * serve and no headless browser should be asked to drive. The emulator's way
 * round this is that it accepts an *unsigned* federated credential: hand it a
 * JSON object where a Google ID token would go and it mints a session for
 * whoever the object claims to be.
 *
 * This runs only when `/api/config` reports an auth emulator, so the deployed
 * app cannot reach it — the real project's Identity Toolkit rejects an
 * unsigned token outright.
 *
 * The uid is fixed rather than random so that a journey can be re-run against
 * a warm emulator and find the cellar it left behind. Override it by putting
 * `?e2eUser=someone` on the URL.
 */
async function signInToEmulator(auth: Auth): Promise<void> {
  const uid = new URLSearchParams(location.search).get('e2eUser') ?? 'journey-taster'
  const claims = {
    sub: uid,
    email: `${uid}@wranker.test`,
    email_verified: true,
    name: 'Journey Taster',
  }
  await signInWithCredential(auth, GoogleAuthProvider.credential(JSON.stringify(claims)))
}

function Splash({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-8 text-center text-stone-400">
      <Glass className="h-12 w-12 animate-pulse text-wine-300" />
      {children}
    </div>
  )
}

export function Glass({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" className={className} aria-hidden="true">
      <path
        d="M176 108h160c0 92-28 130-60 152v100h44a16 16 0 0 1 0 32h-128a16 16 0 0 1 0-32h44V260c-32-22-60-60-60-152Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="18"
        strokeLinejoin="round"
      />
      <path d="M186 150h140c-6 52-24 80-46 96h-48c-22-16-40-44-46-96Z" fill="#9d2235" />
    </svg>
  )
}
